import React, { useState, useRef, useCallback } from 'react';
import { Plus, Trash2, GripVertical } from 'lucide-react';
import { useI18n } from '../utils/i18n';

const TimeblockChecklist = ({
  items = [],
  onChange
}) => {
  const { t } = useI18n();
  const [newItemText, setNewItemText] = useState('');
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const inputRef = useRef(null);

  const handleAdd = (e) => {
    e.preventDefault();
    const trimmed = newItemText.trim();
    if (!trimmed) return;
    const next = [...items, { content: trimmed, sortOrder: items.length }];
    onChange(next);
    setNewItemText('');
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleRemove = (index) => {
    const next = items.filter((_, i) => i !== index);
    onChange(next.map((item, i) => ({ ...item, sortOrder: i })));
  };

  const handleDragStart = (e, index) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
    try {
      const rect = e.currentTarget.getBoundingClientRect();
      e.dataTransfer.setDragImage(e.currentTarget, rect.width / 2, rect.height / 2);
    } catch (_) {}
  };

  const handleDragOver = useCallback((e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOverIndex(null);
  }, []);

  const handleDrop = useCallback((e, dropIndex) => {
    e.preventDefault();
    const dragIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);
    setDragOverIndex(null);
    if (isNaN(dragIndex) || dragIndex === dropIndex) return;
    const next = [...items];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(dropIndex, 0, moved);
    onChange(next.map((item, i) => ({ ...item, sortOrder: i })));
  }, [items, onChange]);

  return (
    <div className="timeblock-checklist">
      <form onSubmit={handleAdd} className="timeblock-checklist-add">
        <input
          ref={inputRef}
          type="text"
          value={newItemText}
          onChange={(e) => setNewItemText(e.target.value)}
          placeholder="Checklisten-Punkt..."
          className="timeblock-checklist-input"
        />
        <button
          type="submit"
          className="timeblock-checklist-add-btn"
          disabled={!newItemText.trim()}
          title={t('common.add', 'Hinzufügen')} aria-label={t('common.add', 'Hinzufügen')}
        >
          <Plus size={14} />
        </button>
      </form>

      {items.length > 0 && (
        <ul className="timeblock-checklist-list">
          {items.map((item, index) => (
            <li
              key={index}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, index)}
              className={`timeblock-checklist-item${dragOverIndex === index ? ' drag-over' : ''}`}
            >
              <span className="timeblock-checklist-text">{item.content}</span>
              <div className="timeblock-checklist-actions">
                <button
                  onClick={() => handleRemove(index)}
                  className="timeblock-checklist-delete"
                  title={t('common.delete', 'Löschen')} aria-label={t('common.delete', 'Löschen')}
                >
                  <Trash2 size={12} />
                </button>
                <div className="timeblock-checklist-drag" title="Ziehen zum Umsortieren">
                  <GripVertical size={12} />
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default TimeblockChecklist;
